/* eslint-disable react-hooks/exhaustive-deps */
import { checkIfRegistered } from '@actions/summit';
import Grow from '@base/Grow/Grow';
import Logo from '@comp/Logo/Logo';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import { getIsLoggedIn } from '@selectors/auth';
import { getImage } from '@utils/imageHelper';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import { Fonts, mobile, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import InnerWrapper from '../ContentContainer';
import RegisterButton from '../RegisterButton';
import SectionDescription from '../SectionDescription';
import { sectionsIds } from '../sections';
import SectionTitle from '../SectionTitle';
import SummitDate from '../SummitDate';
import FixedHeader from './FixedHeader';
import HeaderSections from './HeaderSections';

/* stylelint-disable */
const HeaderWrapper = styled.section`
  width: 100vw;
  height: 100vh;
  max-height: 625px;
  background-color: var(--color-base-0);
  ${({ $isMobile }) =>
    !$isMobile &&
    `background-image: url('${getImage('summit/header_bg_web.svg')}')`}
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: contain;
  padding: ${Spacings.SPACING_0B} 115px;
  /* margin-top: ${Spacings.SPACING_20B}; */
  @media only screen and (max-width: ${mobile.maxWidth}) {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_5B};
    background-size: cover;
    margin-top: ${Spacings.SPACING_0B};
    max-height: 100%;
    height: 100vh;
  }
`;

const StyledInnerWrapper = styled(InnerWrapper)`
  height: 100%;
  display: grid;
  position: relative;
  z-index: 1;
  grid-template: max-content 1fr / 1fr;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    display: block;
  }
`;

const Header = styled.div`
  width: 1136px;
  height: ${Spacings.SPACING_18B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_0B};
  margin: auto;
  margin-bottom: ${Spacings.SPACING_9B};
  display: flex;
  align-items: center;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_0B};
    width: 100%;
    margin-bottom: ${Spacings.SPACING_7B};
  }
`;

const ContentWrapper = styled.div``;

const MoveToAndFro = keyframes`
  from { transform: translateY(${Spacings.SPACING_0B}); }
  to { transform: translateY(-10px); }
`;

const ArrowContainer = styled.div`
  margin: auto;
  animation: ${MoveToAndFro} 2.3s ease-in-out 0s infinite alternate;
  padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_0B};
  display: flex;
  justify-content: center;
  top: 100%;
  position: absolute;
  left: ${Spacings.SPACING_0B};
  right: ${Spacings.SPACING_0B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B};
  }
`;

const ContentContainer = styled.div`
  margin-left: auto !important;
  margin-right: auto !important;
  text-align: center;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    max-width: 100%;
  }
`;

const LogosContainer = styled.div`
  display: grid;
  justify-content: center;
  grid-template: 1fr / max-content max-content;
  grid-column-gap: ${Spacings.SPACING_10B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-top: 8px;
  }
`;

const Image = styled.img`
  user-drag: none;
  height: ${Spacings.SPACING_10B};
`;

const StyledTitle = styled(SectionTitle)`
  max-width: 680px;
  margin: auto;
  margin-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: 40px;
    margin-top: ${Spacings.SPACING_7B};
    padding: ${Spacings.SPACING_0B} 10%;
  }
  @media only screen and (max-width: 320px) {
    padding: ${Spacings.SPACING_0B};
  }
`;

const StyledDescription = styled(SectionDescription)`
  max-width: 620px;
  margin: auto;
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledButton = styled(RegisterButton)`
  margin-left: auto;
  margin-right: auto;
`;

const RegisterWrapper = styled.div`
  text-align: center;
`;

// const PulseVideo = styled.video`
//   position: absolute;
//   width: 259px;
//   left: 0px;
//   right: 0px;
//   margin: auto;
//   bottom: 0px;
// `;

const RegisteredCnf = styled(SectionDescription)`
  margin-top: ${Spacings.SPACING_4B};
  text-align: center;
`;

const StyledLogo = styled(Logo)`
  display: inline-block;
`;

const SummitHeader = ({ headerRefs, isPageReady }) => {
  const isRegtd = useSelector(({ summit }) => summit.isRegistered);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [isRegistered, setIsRegistered] = useState(isRegtd);
  const dispatch = useDispatch();
  const summitUID = useSelector(({ summit }) => summit.info.uid);
  useEffect(() => {
    dispatch(checkIfRegistered(summitUID)).then((response) => {
      if (response && response.result) {
        const courseRegistered = response.result.is_registered;
        setIsRegistered(courseRegistered);
        if (courseRegistered) {
          dispatch({
            type: 'REGISTER_SUCCESS'
          });
        }
      }
    });
  }, [isLoggedIn]);

  useEffect(() => {
    setIsRegistered(isRegtd);
  }, [isRegtd]);

  const isMobileDevice = useIsMobile();
  const staticTexts = useSelector(({ summit }) => summit.staticTexts);
  const logos = useSelector(({ summit }) => summit.info?.logos || {});
  const mainLogo = logos?.main?.url ? `${logos?.main.url}` : null;
  const subLogo = logos?.sublogo?.url ? `${logos?.sublogo?.url}` : null;
  return (
    <HeaderWrapper $isMobile={isMobileDevice}>
      <FixedHeader headerRefs={headerRefs} isPageReady={isPageReady} />
      <Header>
        <StyledLogo isReload />
        {!isMobileDevice && (
          <>
            <Grow />
            <HeaderSections headerRefs={headerRefs} />
          </>
        )}
      </Header>
      <StyledInnerWrapper>
        <ContentWrapper>
          <ContentContainer>
            <LogosContainer>
              {mainLogo && <Image src={mainLogo} alt="logo" />}
              {subLogo && <Image src={subLogo} alt="logo" />}
            </LogosContainer>
            <StyledTitle>{staticTexts.mainHeader || ''}</StyledTitle>
            <StyledDescription $size={Fonts.H4}>
              {staticTexts.mainDesc || ''}
            </StyledDescription>
          </ContentContainer>
          <RegisterWrapper>
            <SummitDate />
            <StyledButton scheduleRef={headerRefs[sectionsIds.schedule]} />
          </RegisterWrapper>
          {isRegistered && (
            <RegisteredCnf $size={Fonts.H4}>
              {staticTexts.registrationSuccess || ''}
              &nbsp;
              <span role="img" aria-label="party">
                &#x1F389;
              </span>
            </RegisteredCnf>
          )}
        </ContentWrapper>
        {!isMobileDevice && (
          <ArrowContainer $isMobile={isMobileDevice}>
            <KeyboardArrowDown
              size={Fonts.H3}
              color="var(--color-text-primary)"
            />
          </ArrowContainer>
        )}
      </StyledInnerWrapper>
    </HeaderWrapper>
  );
};

SummitHeader.propTypes = {
  headerRefs: PropTypes.objectOf(PropTypes.object).isRequired,
  isPageReady: PropTypes.bool.isRequired
};

export default SummitHeader;
